// Nombres de la cadena para las pantallas de comisiones: el motor guarda ids, y los nombres de
// tiendas, zonas y vendedores viven en retail-org; los de los productos, en retail-catalog.
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    listProducts,
    type CatalogProduct,
} from '@/api/services/retail-catalog/retail-catalog.client';
import type { Store } from '@/api/services/retail-org/retail-org.client';
import {
    useRosterRetailer,
    useRosterStores,
    useRosterUsers,
    useRosterZones,
} from '@/domains/retail/admin/lib/useRoster';
import { commissionKeys } from './commissionQueries';

export interface CommissionDirectory {
    retailerId: string | null;
    stores: Store[];
    products: CatalogProduct[];
    isLoading: boolean;
    storeName: (storeId: string) => string;
    storeZone: (storeId: string) => string | null;
    sellerName: (sellerId: string) => string;
    sellerStoreId: (sellerId: string) => string | null;
    /** Es el encargado de alguna tienda (★ "Admin tienda" del mock). */
    isStoreManager: (sellerId: string) => boolean;
    product: (sku: string) => CatalogProduct | undefined;
}

export function useCommissionDirectory(): CommissionDirectory {
    const { retailerId } = useRosterRetailer();
    const stores = useRosterStores(retailerId);
    const zones = useRosterZones(retailerId);
    const users = useRosterUsers(retailerId);
    const products = useQuery({
        queryKey: [...commissionKeys.all(), 'products', retailerId],
        queryFn: () => listProducts(retailerId ?? ''),
        enabled: retailerId !== null,
        staleTime: 5 * 60_000,
    });

    const isLoading = stores.isLoading || users.isLoading || products.isLoading;
    return useMemo(() => {
        const storeList = stores.data ?? [];
        const storeById = new Map(storeList.map((store) => [store.storeId, store]));
        const zoneById = new Map((zones.data ?? []).map((zone) => [zone.zoneId, zone.name]));
        const userById = new Map((users.data ?? []).map((user) => [user.retailUserId, user]));
        const productBySku = new Map(
            (products.data ?? []).map((product) => [product.sku, product]),
        );
        const managers = new Set(
            storeList.flatMap((store) =>
                store.managerRetailUserId ? [store.managerRetailUserId] : [],
            ),
        );
        return {
            retailerId,
            stores: storeList,
            products: products.data ?? [],
            isLoading,
            storeName: (storeId) => storeById.get(storeId)?.name ?? storeId,
            storeZone: (storeId) => {
                const zoneId = storeById.get(storeId)?.zoneId;
                return zoneId ? (zoneById.get(zoneId) ?? null) : null;
            },
            sellerName: (sellerId) => userById.get(sellerId)?.displayName ?? sellerId,
            sellerStoreId: (sellerId) => userById.get(sellerId)?.homeStoreId ?? null,
            isStoreManager: (sellerId) => managers.has(sellerId),
            product: (sku) => productBySku.get(sku),
        };
    }, [retailerId, stores.data, zones.data, users.data, products.data, isLoading]);
}
